import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faMoon,
  faSun,
  faUser,
  faHeart,
  faTachometerAlt,
  faImage,
  faFileAlt,
  faCog,
  faSignOutAlt,
  faGlobe,
  faBars,
  faMugHot,
  faPlus,
  faChalkboardTeacher, // Ícono para Cursos
} from '@fortawesome/free-solid-svg-icons';

import { useAuth } from '../../context/UseAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { useLanguageLink } from '../../hooks/useLanguageLink';
// import { useOwnProfile } from '../../hooks/useOwnProfile'; // Quitamos si no se usa

import UserAvatar from '../common/UserAvatar';
import AdminMenu from './AdminMenu';
import NotificationsPanel from './NotificationsPanel';
import UserDisplayInfo from '../common/UserDisplayInfo';

import CreatePostModal from '../../pages/Posts/CreatePostModal';
import CreatePackModal from '../../pages/Packs/CreatePackModal';

import logoLight from '/images/logo.svg';
import logoDark from '/images/logo-dark.svg';

// (Componente LanguageSwitcher sin cambios...)
const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const changeLanguage = (lng) => {
    setMenuOpen(false);
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.startsWith(`/${i18n.language}`)
      ? currentPath.substring(i18n.language.length + 1)
      : currentPath;
    navigate(`/${lng}${pathWithoutLang}${location.search}`);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target))
        setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700"
        aria-label="Change language"
      >
        <FontAwesomeIcon icon={faGlobe} className="text-lg" />
      </button>
      {menuOpen && (
        <div className="absolute right-0 mt-2 w-36 bg-white dark:bg-neutral-800 shadow-xl rounded-lg z-50 border border-neutral-200 dark:border-neutral-700">
          <button
            onClick={() => changeLanguage('es')}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 ${
              i18n.language === 'es' ? 'font-bold text-primary' : ''
            }`}
          >
            Español
          </button>
          <button
            onClick={() => changeLanguage('en')}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 ${
              i18n.language === 'en' ? 'font-bold text-primary' : ''
            }`}
          >
            English
          </button>
          <button
            onClick={() => changeLanguage('pt')}
            className={`w-full text-left px-4 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 ${
              i18n.language === 'pt' ? 'font-bold text-primary' : ''
            }`}
          >
            Português
          </button>
        </div>
      )}
    </div>
  );
};

const Navbar = ({ onToggleSidebar }) => {
  const { t } = useTranslation();
  const { isAuthenticated, logout, user } = useAuth();
  const navigate = useNavigate();
  const { langLink } = useLanguageLink();

  const [darkMode, setDarkMode] = useState(() =>
    document.documentElement.classList.contains('dark')
  );
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isCreateMenuOpen, setCreateMenuOpen] = useState(false);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [isPackModalOpen, setIsPackModalOpen] = useState(false);

  const profileMenuRef = useRef(null);
  const notificationsButtonRef = useRef(null);
  const notificationsPanelRef = useRef(null);
  const createMenuRef = useRef(null);

  const { unreadCount, markAsRead } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isNotificationsOpen &&
        notificationsButtonRef.current &&
        !notificationsButtonRef.current.contains(event.target) &&
        notificationsPanelRef.current &&
        !notificationsPanelRef.current.contains(event.target)
      ) {
        setIsNotificationsOpen(false);
      }
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(event.target)
      ) {
        setProfileMenuOpen(false);
      }
      if (
        createMenuRef.current &&
        !createMenuRef.current.contains(event.target)
      ) {
        setCreateMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isNotificationsOpen]);

  const handleToggleNotifications = () => {
    setIsNotificationsOpen((prev) => {
      if (!prev && unreadCount > 0) markAsRead();
      return !prev;
    });
  };

  const closeNotifications = () => setIsNotificationsOpen(false);

  const toggleDarkMode = () => {
    setDarkMode((prevMode) => {
      const newMode = !prevMode;
      document.documentElement.classList.toggle('dark', newMode);
      localStorage.setItem('theme', newMode ? 'dark' : 'light');
      return newMode;
    });
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      document.documentElement.classList.add('dark');
      setDarkMode(true);
    } else {
      document.documentElement.classList.remove('dark');
      setDarkMode(false);
    }
  }, []);

  const handleLogout = () => {
    logout();
    setProfileMenuOpen(false);
    navigate('/');
  };

  const displayUser = user;
  const isCreator = displayUser?.profileType === 'creator';
  const isAdmin = displayUser?.profileType === 'admin';
  const cursosCategoryId = '68b095babef356dc6d3dd52d';
  const isCourseCreator = isCreator && user?.category._id === cursosCategoryId;

  const openPostModal = () => {
    setCreateMenuOpen(false);
    setIsPostModalOpen(true);
  };
  const openPackModal = () => {
    setCreateMenuOpen(false);
    setIsPackModalOpen(true);
  };
  const openCourseCreation = () => {
    setCreateMenuOpen(false);
    console.log('Abrir modal/página de creación de curso');
    // navigate(langLink('/my/courses/create'));
  };

  return (
    <>
      {/* Portal Notificaciones */}
      {isNotificationsOpen &&
        createPortal(
          <>
            <div
              className={`fixed inset-0 bg-black/60 z-[98] transition-opacity lg:hidden ${
                isNotificationsOpen
                  ? 'opacity-100'
                  : 'opacity-0 pointer-events-none'
              }`}
              onClick={closeNotifications}
              aria-hidden="true"
            />
            <div
              className={`fixed top-0 right-0 h-full w-full max-w-sm z-[99] transition-transform duration-300 ease-in-out lg:hidden ${
                isNotificationsOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              <NotificationsPanel onClose={closeNotifications} />
            </div>
            <div
              ref={notificationsPanelRef}
              className={`fixed z-[99] w-96 max-w-none origin-top-right transition-all hidden lg:block ${
                isNotificationsOpen
                  ? 'scale-100 opacity-100'
                  : 'scale-95 opacity-0 pointer-events-none'
              }`}
              style={{
                top: notificationsButtonRef.current
                  ? `${
                      notificationsButtonRef.current.getBoundingClientRect()
                        .bottom + 8
                    }px`
                  : '0',
                right: notificationsButtonRef.current
                  ? `${
                      window.innerWidth -
                      notificationsButtonRef.current.getBoundingClientRect()
                        .right
                    }px`
                  : '0',
              }}
            >
              <NotificationsPanel onClose={closeNotifications} />
            </div>
          </>,
          document.getElementById('portal-root')
        )}

      {/* Header Principal */}
      <header className="sticky top-0 z-30 w-full bg-white/80 dark:bg-neutral-900/80 backdrop-blur-lg border-b border-neutral-200 dark:border-neutral-700">
        <div className="container-main h-16 flex justify-between items-center">
          {/* Lado Izquierdo */}
          <div className="flex items-center gap-2 md:gap-4">
            <button
              onClick={onToggleSidebar}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-xl"
              aria-label="Toggle Menu"
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
            <Link to={langLink('/')} className="flex items-center gap-3">
              <img
                src={darkMode ? logoDark : logoLight}
                alt={`${t('common.appName')} logo`}
                className="h-8 w-auto"
              />
              <span className="font-display font-bold text-2xl hidden sm:inline text-neutral-900 dark:text-white">
                Unloquea
              </span>
            </Link>
          </div>

          {/* Lado Derecho */}
          <div className="flex items-center gap-2">
            {isAuthenticated && displayUser ? (
              <>
                {/* Menú Crear */}
                {isCreator && (
                  <div className="relative hidden lg:block" ref={createMenuRef}>
                    <button
                      onClick={() => setCreateMenuOpen((prev) => !prev)}
                      className="btn-primary flex items-center gap-2 !px-3 !py-2"
                      aria-label={t('navbar.create.label', 'Crear contenido')}
                    >
                      <FontAwesomeIcon icon={faPlus} className="text-lg" />
                      <span className="text-sm font-semibold">
                        {t('common.create', 'Crear')}
                      </span>
                    </button>
                    {isCreateMenuOpen && (
                      <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-neutral-800 shadow-xl rounded-xl z-50 border border-neutral-200 dark:border-neutral-700 overflow-hidden p-2">
                        {isCourseCreator ? (
                          <button
                            onClick={openCourseCreation}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition text-left"
                          >
                            <FontAwesomeIcon
                              icon={faChalkboardTeacher}
                              className="w-5 text-center text-neutral-500"
                            />
                            <span>
                              {t('navbar.create.course', 'Crear Curso')}
                            </span>
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={openPostModal}
                              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition text-left"
                            >
                              <FontAwesomeIcon
                                icon={faFileAlt}
                                className="w-5 text-center text-neutral-500"
                              />
                              <span>
                                {t('navbar.create.post', 'Crear Post')}
                              </span>
                            </button>
                            <button
                              onClick={openPackModal}
                              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition text-left"
                            >
                              <FontAwesomeIcon
                                icon={faImage}
                                className="w-5 text-center text-neutral-500"
                              />
                              <span>
                                {t('navbar.create.pack', 'Crear Galería')}
                              </span>
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {isAdmin && <AdminMenu />}

                {/* Botón Notificaciones */}
                <div ref={notificationsButtonRef}>
                  <button
                    onClick={handleToggleNotifications}
                    className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                    aria-label="Notifications"
                  >
                    <FontAwesomeIcon icon={faBell} className="text-xl" />
                    {unreadCount > 0 && (
                      <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full border-2 border-white dark:border-neutral-900">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                </div>

                {/* Menú Perfil */}
                <div className="relative" ref={profileMenuRef}>
                  <button
                    onClick={() => setProfileMenuOpen((o) => !o)}
                    className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                  >
                    <UserAvatar size="sm" user={displayUser} />
                  </button>
                  {profileMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-neutral-800 shadow-xl rounded-xl z-50 border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                      <div className="flex items-center gap-3 p-4 border-b border-neutral-200 dark:border-neutral-700">
                        <UserAvatar size="md" user={displayUser} />
                        <UserDisplayInfo user={displayUser} link={false} />
                      </div>
                      <nav className="p-2 text-neutral-700 dark:text-neutral-300">
                        <Link
                          onClick={() => setProfileMenuOpen(false)}
                          to={langLink('/my/profile')}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                        >
                          <FontAwesomeIcon
                            icon={faUser}
                            className="w-5 text-center text-neutral-500"
                          />{' '}
                          {t('navbar.myProfile')}
                        </Link>
                        <Link
                          onClick={() => setProfileMenuOpen(false)}
                          to={langLink('/my/purchases')}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                        >
                          <FontAwesomeIcon
                            icon={faHeart}
                            className="w-5 text-center text-neutral-500"
                          />{' '}
                          {t('navbar.myPurchases')}
                        </Link>
                        <Link
                          onClick={() => setProfileMenuOpen(false)}
                          to={langLink('/my/donations')}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                        >
                          <FontAwesomeIcon
                            icon={faMugHot}
                            className="w-5 text-center text-neutral-500"
                          />{' '}
                          {t('navbar.myDonations')}
                        </Link>
                        {isCreator && (
                          <>
                            <Link
                              onClick={() => setProfileMenuOpen(false)}
                              to={langLink('/dashboard')}
                              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                            >
                              <FontAwesomeIcon
                                icon={faTachometerAlt}
                                className="w-5 text-center text-neutral-500"
                              />{' '}
                              {t('navbar.dashboard')}
                            </Link>
                            {isCourseCreator ? (
                              <Link
                                onClick={() => setProfileMenuOpen(false)}
                                to={langLink('/my/courses')}
                                className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                              >
                                <FontAwesomeIcon
                                  icon={faChalkboardTeacher}
                                  className="w-5 text-center text-neutral-500"
                                />{' '}
                                {t('navbar.myCourses', 'Mis Cursos')}
                              </Link>
                            ) : (
                              <>
                                <Link
                                  onClick={() => setProfileMenuOpen(false)}
                                  to={langLink('/my/posts')}
                                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                                >
                                  <FontAwesomeIcon
                                    icon={faFileAlt}
                                    className="w-5 text-center text-neutral-500"
                                  />{' '}
                                  {t('navbar.myPosts')}
                                </Link>
                                <Link
                                  onClick={() => setProfileMenuOpen(false)}
                                  to={langLink('/my/packs')}
                                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                                >
                                  <FontAwesomeIcon
                                    icon={faImage}
                                    className="w-5 text-center text-neutral-500"
                                  />{' '}
                                  {t('navbar.myGalleries')}
                                </Link>
                              </>
                            )}
                          </>
                        )}
                        <div className="my-2 border-t border-neutral-200 dark:border-neutral-700"></div>
                        <Link
                          onClick={() => setProfileMenuOpen(false)}
                          to={langLink('/my/settings')}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                        >
                          <FontAwesomeIcon
                            icon={faCog}
                            className="w-5 text-center text-neutral-500"
                          />{' '}
                          {t('navbar.settings')}
                        </Link>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-red-600 dark:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition"
                        >
                          <FontAwesomeIcon
                            icon={faSignOutAlt}
                            className="w-5 text-center"
                          />{' '}
                          {t('navbar.logout')}
                        </button>
                      </nav>
                    </div>
                  )}
                </div>
              </>
            ) : (
              // Si no está autenticado
              <>
                <Link
                  to={langLink('/login')}
                  className="px-4 py-2 rounded-full text-sm font-semibold text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                >
                  {t('navbar.login')}
                </Link>
                <Link
                  to={langLink('/register')}
                  className="btn-primary"
                  style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  {t('navbar.register')}
                </Link>
              </>
            )}
            {/* Dark Mode y Language Switcher */}
            <button
              onClick={toggleDarkMode}
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700"
              aria-label="Toggle dark mode"
            >
              <FontAwesomeIcon
                icon={darkMode ? faSun : faMoon}
                className="text-xl"
              />
            </button>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Modales de Creación */}
      {isPostModalOpen && (
        <CreatePostModal
          onClose={() => setIsPostModalOpen(false)}
          // --- INICIO CORRECCIÓN ---
          // Usar _newPost para indicar que no se usa el argumento
          onSuccess={() => {
            // --- FIN CORRECCIÓN ---
            setIsPostModalOpen(false);
            navigate(langLink('/my/posts'));
          }}
        />
      )}
      {isPackModalOpen && (
        <CreatePackModal
          onClose={() => setIsPackModalOpen(false)}
          // --- INICIO CORRECCIÓN ---
          // Usar _newPack para indicar que no se usa el argumento
          onSuccess={() => {
            // --- FIN CORRECCIÓN ---
            setIsPackModalOpen(false);
            navigate(langLink('/my/packs'));
          }}
        />
      )}
    </>
  );
};

export default Navbar;
